/* eslint-disable @typescript-eslint/no-empty-function */
import {
  Component,
  HostBinding,
  Input,
  Output,
  EventEmitter,
  forwardRef,
  OnInit,
  OnDestroy
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  FormControl,
  ReactiveFormsModule
} from '@angular/forms';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { UmssIconComponent } from '../icon/icon.component';

type BaseInputType = 'text' | 'email' | 'tel' | 'url' | 'search';

export type InputType = BaseInputType | 'password' | 'number';

export type InputMode = BaseInputType | 'none' | 'numeric' | 'decimal';

@Component({
  selector: 'umss-input',
  standalone: true,
  imports: [
    CommonModule,
    UmssIconComponent,
    NgxMaskDirective,
    ReactiveFormsModule
  ],
  templateUrl: './input.component.html',
  styleUrls: ['./input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UmssInputComponent),
      multi: true
    },
    provideNgxMask()
  ]
})
export class UmssInputComponent
  implements ControlValueAccessor, OnInit, OnDestroy
{
  @HostBinding('style.display') display = 'contents';

  private readonly _fallbackInputId = `umss-input-${Math.random().toString(36).slice(2)}`;
  get inputId(): string {
    return this.id ?? this._fallbackInputId;
  }

  /** Internal FormControl - used for both masked and unmasked inputs */
  inputControl = new FormControl<string>('', { nonNullable: true });
  private inputSubscription?: { unsubscribe: () => void };

  ngOnInit(): void {
    this.inputSubscription = this.inputControl.valueChanges.subscribe(
      (value) => {
        this._value = value;
        this._onChange(value);
        this.valueChange.emit(value);
        this.onInput.emit(value);
      }
    );
  }

  ngOnDestroy(): void {
    this.inputSubscription?.unsubscribe();
  }

  /** Content props */
  @Input() label?: string;
  @Input() placeholder: string = '';
  @Input() helperText?: string;
  @Input() errorMessage?: string;
  @Input() type: InputType = 'text';

  /** Icon props */
  @Input() prefixIconName?: string; // Phosphor icon name for prefix icon
  @Input() suffixIconName?: string; // Phosphor icon name for suffix icon

  /** Text suffix/prefix props */
  @Input() suffix?: string; // Text suffix (e.g., "/hr", "USD")
  @Input() prefix?: string; // Text prefix

  /** Variant props */
  @Input() size: 'Mini' | 'Small' | 'Regular' | 'Large' = 'Regular';

  /** State props */
  @Input() disabled: boolean = false;
  @Input() required: boolean = false;
  @Input() readonly: boolean = false;
  @Input() error: boolean = false;

  /** Native HTML input attributes */
  @Input() name?: string;
  @Input() id?: string;
  @Input() autocomplete?: string;
  @Input() maxlength?: number;
  @Input() minlength?: number;
  @Input() pattern?: string;

  private _customInputmode?: InputMode;
  @Input() set inputmode(value: InputMode | undefined) {
    this._customInputmode = value;
  }
  get inputmode(): InputMode | undefined {
    return this._customInputmode;
  }

  get appliedInputmode(): InputMode | undefined {
    if (this._customInputmode) return this._customInputmode;

    switch (this.type) {
      case 'email':
        return 'email';
      case 'tel':
        return 'tel';
      case 'url':
        return 'url';
      case 'number':
        return 'numeric';
      case 'search':
        return 'search';
      default:
        return undefined;
    }
  }

  @Input() min?: string | number;
  @Input() max?: string | number;
  @Input() step?: string | number;
  @Input() accept?: string;
  @Input() multiple?: boolean;
  @Input() autofocus?: boolean;
  @Input() tabindex?: number;

  private _customMask?: string;
  @Input() set mask(value: string | undefined) {
    this._customMask = value;
  }
  get mask(): string | undefined {
    return this._customMask;
  }

  get appliedMask(): string | undefined {
    if (this._customMask) return this._customMask;

    if (this.type === 'tel' || this.appliedInputmode === 'tel')
      return '(000) 000-0000';

    return undefined;
  }

  /** Value prop */
  private _value: string = '';
  @Input() set value(val: string) {
    this._value = val;
  }
  get value(): string {
    return this._value;
  }

  /** Password visibility toggle */
  showPassword = false;

  /** Event outputs */
  @Output() valueChange = new EventEmitter<string>();
  @Output() onFocus = new EventEmitter<void>();
  @Output() onBlur = new EventEmitter<void>();
  @Output() onInput = new EventEmitter<string>();
  @Output() prefixIconClick = new EventEmitter<void>();
  @Output() suffixIconClick = new EventEmitter<void>();

  /** ControlValueAccessor implementation */
  private _onChange: (value: string) => void = () => {};
  private _onTouched: () => void = () => {};

  writeValue(value: string): void {
    const rawValue = value || '';
    this._value = rawValue;
    this.inputControl.setValue(rawValue, { emitEvent: false });
  }

  registerOnChange(fn: (value: string) => void): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    if (isDisabled) this.inputControl.disable({ emitEvent: false });
    else this.inputControl.enable({ emitEvent: false });
  }

  onInputFocus(): void {
    if (this.disabled) return;
    this.onFocus.emit();
  }

  onInputBlur(): void {
    if (this.disabled) return;
    this._onTouched();
    this.onBlur.emit();
  }

  onPrefixIconClick(): void {
    if (this.disabled) return;
    this.prefixIconClick.emit();
  }

  onSuffixIconClick(): void {
    if (this.disabled) return;
    this.suffixIconClick.emit();
  }

  /** Computed properties */
  get hasValue(): boolean {
    return !!(this._value && this._value.length > 0);
  }

  get iconSize(): string {
    switch (this.size) {
      case 'Mini':
        return '14px';
      case 'Small':
        return '16px';
      case 'Regular':
        return '18px';
      case 'Large':
        return '20px';
      default:
        return '18px';
    }
  }

  get inputType(): string {
    if (this.type === 'password' && this.showPassword) {
      return 'text';
    }
    return this.type;
  }

  get passwordToggleIcon(): string {
    return this.showPassword ? 'phosphorEyeClosed' : 'phosphorEye';
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }
}
